import { useState, useEffect, useMemo } from 'react'
import {
  ActionIcon,
  Modal,
  TextInput,
  Button,
  Group,
  Text,
  Table,
  Badge,
  Checkbox,
  Collapse,
  Stack,
} from '@mantine/core'
import { IconSearch, IconChevronDown, IconChevronRight, IconX } from '@tabler/icons-react'
import type { CustomTestPanel } from './customPanelStorage'

interface TestDef {
  code: string
  name: string
  specimen?: string
}

interface CustomPanelModalProps {
  opened: boolean
  onClose: () => void
  onSave: (name: string, testCodes: string[]) => void
  existingPanel?: CustomTestPanel | null
  saving?: boolean
  testGroups: { category: string; tests: TestDef[] }[]
}

export function CustomPanelModal({
  opened,
  onClose,
  onSave,
  existingPanel,
  saving,
  testGroups,
}: CustomPanelModalProps) {
  const [panelName, setPanelName] = useState('')
  const [selectedCodes, setSelectedCodes] = useState<string[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set())

  // Reset form when modal opens
  useEffect(() => {
    if (opened) {
      setPanelName(existingPanel?.name ?? '')
      setSelectedCodes(existingPanel?.testCodes ?? [])
      setSearchQuery('')
      setExpandedSections(new Set(testGroups.map((g) => g.category)))
    }
  }, [opened, existingPanel, testGroups])

  const toggleCode = (code: string) => {
    setSelectedCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    )
  }

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev)
      if (next.has(section)) next.delete(section)
      else next.add(section)
      return next
    })
  }

  const filteredGroups = useMemo(() => {
    if (!searchQuery.trim()) return testGroups
    const q = searchQuery.toLowerCase()
    return testGroups
      .map((group) => ({
        ...group,
        tests: group.tests.filter(
          (t) => t.name.toLowerCase().includes(q) || t.code.toLowerCase().includes(q),
        ),
      }))
      .filter((group) => group.tests.length > 0)
  }, [searchQuery, testGroups])

  // Auto-expand when searching
  useEffect(() => {
    if (!searchQuery.trim()) return
    setExpandedSections((prev) => {
      const next = new Set(prev)
      for (const group of filteredGroups) {
        next.add(group.category)
      }
      return next
    })
  }, [searchQuery, filteredGroups])

  const nameError = panelName.trim() === '' ? 'Panel name is required' : undefined
  const canSave = panelName.trim() !== '' && selectedCodes.length > 0

  const handleSave = () => {
    if (!canSave) return
    onSave(panelName.trim(), selectedCodes)
  }

  const renderTestTable = (tests: TestDef[]) => (
    <Table withTableBorder withColumnBorders>
      <Table.Thead>
        <Table.Tr>
          <Table.Th w={40} />
          <Table.Th w={70}>Code</Table.Th>
          <Table.Th>Test Name</Table.Th>
          <Table.Th w={90}>Specimen</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {tests.map((test) => (
          <Table.Tr key={test.code}>
            <Table.Td>
              <Checkbox
                checked={selectedCodes.includes(test.code)}
                onChange={() => toggleCode(test.code)}
              />
            </Table.Td>
            <Table.Td>
              <Text size="xs" className="font-mono">
                {test.code}
              </Text>
            </Table.Td>
            <Table.Td>
              <Text size="xs" fw={500}>
                {test.name}
              </Text>
            </Table.Td>
            <Table.Td>
              <Badge size="xs" variant="light" color="gray">
                {test.specimen}
              </Badge>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  )

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={existingPanel ? 'Edit Panel' : 'Create New Panel'}
      size="xl"
      centered
    >
      <Stack gap="md">
        <TextInput
          label="Panel Name"
          placeholder="e.g. Diabetes Workup"
          value={panelName}
          onChange={(e) => setPanelName(e.target.value)}
          error={panelName !== '' || !existingPanel ? undefined : nameError}
          required
        />

        <div>
          <Group justify="space-between" mb="xs">
            <Text fw={600} size="sm">
              Select Individual Tests
            </Text>
            <Badge size="sm" variant="light" color={selectedCodes.length > 0 ? 'blue' : 'gray'}>
              {selectedCodes.length} selected
            </Badge>
          </Group>

          <TextInput
            placeholder="Search by test name or code..."
            leftSection={<IconSearch size={16} />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            mb="sm"
            rightSection={
              searchQuery ? (
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  aria-label="Clear search"
                  onClick={() => setSearchQuery('')}
                >
                  <IconX size={14} />
                </ActionIcon>
              ) : null
            }
            rightSectionPointerEvents={searchQuery ? 'all' : 'none'}
          />

          <div className="max-h-[400px] overflow-y-auto">
            <Stack gap="xs">
              {filteredGroups.map((group) => (
                <div key={group.category}>
                  <Button
                    variant="subtle"
                    size="sm"
                    fullWidth
                    justify="space-between"
                    rightSection={
                      expandedSections.has(group.category) ? (
                        <IconChevronDown size={16} />
                      ) : (
                        <IconChevronRight size={16} />
                      )
                    }
                    onClick={() => toggleSection(group.category)}
                    className="!text-left"
                  >
                    <Group gap="xs">
                      <Text fw={600} size="sm">
                        {group.category}
                      </Text>
                      <Badge size="xs" variant="light">
                        {group.tests.length} tests
                      </Badge>
                    </Group>
                  </Button>
                  <Collapse in={expandedSections.has(group.category)}>
                    <div className="mr-4 ml-4">{renderTestTable(group.tests)}</div>
                  </Collapse>
                </div>
              ))}
              {filteredGroups.length === 0 && (
                <Text size="sm" c="dimmed" ta="center" py="md">
                  No tests match your search.
                </Text>
              )}
            </Stack>
          </div>
        </div>

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSave} loading={saving}>
            {existingPanel ? 'Save Changes' : 'Create Panel'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
